/**
 * LLM Health Monitor
 *
 * Admin tab for monitoring LLM call health: success/failure rates by model,
 * error logs with diagnostics, and cost tracking.
 *
 * Endpoints consumed:
 *   GET /api/admin/llm-health/status-summary?days=
 *   GET /api/admin/llm-health/error-log?days=&limit=&offset=&event_type=&call_name=&model=
 *   GET /api/admin/llm-health/error-log/{event_id}/diagnostic
 *   GET /api/admin/llm-health/event-types?days=
 */

import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { storage } from '../../services/storage';
import { API_BASE_URL } from '../../utils/apiConfig';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

// ── API helper ──────────────────────────────────────────────────────────────

async function apiFetch<T>(path: string): Promise<T> {
  const token = await storage.getItem('admin_token');
  if (!token) throw new Error('Admin token not found. Please log in again.');

  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

// ── Types ───────────────────────────────────────────────────────────────────

interface ModelSummary {
  model: string;
  successful_calls: number;
  unsuccessful_calls: number;
  total_cost: number;
  error_rate_pct: number;
  error_breakdown: Record<string, number>;
}

interface StatusSummary {
  by_model: ModelSummary[];
  totals: {
    successful_calls: number;
    unsuccessful_calls: number;
    total_cost: number;
    error_rate_pct: number;
  };
}

interface ErrorEvent {
  event_id: string;
  timestamp: string;
  call_name: string;
  model: string;
  event_type: string;
  error_message: string;
  severity: string;
  additional_context: Record<string, any> | null;
}

interface ErrorLogResponse {
  events: ErrorEvent[];
  total: number;
  limit: number;
  offset: number;
}

interface EventTypeCount {
  event_type: string;
  count: number;
}

// ── Theme constants ─────────────────────────────────────────────────────────

const COLORS = {
  bg: '#1a1a2e',
  card: '#16213e',
  cardHover: '#1a2744',
  border: '#2a3a5c',
  text: '#ffffff',
  textMuted: '#8892b0',
  textDim: '#5a6785',
  green: '#10B981',
  greenDim: '#065F46',
  yellow: '#F59E0B',
  yellowDim: '#78350F',
  red: '#EF4444',
  redDim: '#7F1D1D',
  blue: '#3B82F6',
  purple: '#8B5CF6',
  accent: '#10B981',
};

const CHART_COLORS = ['#10B981', '#EF4444'];

// ── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n: number, d = 2) => `$${n.toFixed(d)}`;
const fmtPct = (n: number) => `${n.toFixed(2)}%`;
const fmtNum = (n: number) => n.toLocaleString();
const truncate = (s: string, len: number) => s.length > len ? s.slice(0, len) + '...' : s;

function errorRateColor(pct: number): string {
  if (pct < 0.5) return COLORS.green;
  if (pct <= 2) return COLORS.yellow;
  return COLORS.red;
}

function errorRateBg(pct: number): string {
  if (pct < 0.5) return COLORS.greenDim;
  if (pct <= 2) return COLORS.yellowDim;
  return COLORS.redDim;
}

function severityColor(severity: string): string {
  switch (severity?.toLowerCase()) {
    case 'critical': return COLORS.red;
    case 'high': return '#F97316';
    case 'medium': return COLORS.yellow;
    case 'low': return COLORS.blue;
    default: return COLORS.textMuted;
  }
}

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return ts;
  }
}

// ── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  container: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    color: COLORS.text,
  } as React.CSSProperties,
  section: {
    marginBottom: 32,
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: COLORS.text,
    marginBottom: 16,
  } as React.CSSProperties,
  pillRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 20,
  } as React.CSSProperties,
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
    marginBottom: 24,
  } as React.CSSProperties,
  card: {
    background: COLORS.card,
    borderRadius: 10,
    padding: '16px 20px',
    border: `1px solid ${COLORS.border}`,
  } as React.CSSProperties,
  cardLabel: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginBottom: 6,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    fontWeight: 500,
  } as React.CSSProperties,
  cardValue: {
    fontSize: 24,
    fontWeight: 700,
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: 13,
  } as React.CSSProperties,
  th: {
    textAlign: 'left' as const,
    padding: '10px 12px',
    fontSize: 11,
    fontWeight: 600,
    color: COLORS.textMuted,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    borderBottom: `1px solid ${COLORS.border}`,
  } as React.CSSProperties,
  td: {
    padding: '10px 12px',
    borderBottom: `1px solid ${COLORS.border}`,
    color: COLORS.text,
  } as React.CSSProperties,
  filterRow: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  } as React.CSSProperties,
  input: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 6,
    padding: '8px 12px',
    color: COLORS.text,
    fontSize: 13,
    outline: 'none',
    minWidth: 160,
  } as React.CSSProperties,
  select: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 6,
    padding: '8px 12px',
    color: COLORS.text,
    fontSize: 13,
    outline: 'none',
    cursor: 'pointer',
    minWidth: 140,
  } as React.CSSProperties,
  expandedRow: {
    background: '#0f1729',
    padding: 16,
    borderRadius: 6,
    fontSize: 12,
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const,
    color: COLORS.textMuted,
    maxHeight: 300,
    overflow: 'auto',
  } as React.CSSProperties,
  badge: (bg: string, fg: string) => ({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
    background: bg,
    color: fg,
  } as React.CSSProperties),
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 16,
    fontSize: 13,
    color: COLORS.textMuted,
  } as React.CSSProperties,
};

// ── Component ───────────────────────────────────────────────────────────────

interface LLMHealthMonitorProps {
  startDate?: Date;
  endDate?: Date;
}

export const LLMHealthMonitor: React.FC<LLMHealthMonitorProps> = ({ startDate, endDate }) => {
  const hasDateProps = startDate !== undefined && endDate !== undefined;

  // Summary state — internal days used only when no date props provided
  const [days, setDays] = useState(7);
  const [summary, setSummary] = useState<StatusSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // Error log state
  const [logDays, setLogDays] = useState(1);
  const [logLimit] = useState(50);
  const [logOffset, setLogOffset] = useState(0);
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [callNameFilter, setCallNameFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [errorLog, setErrorLog] = useState<ErrorLogResponse | null>(null);
  const [logLoading, setLogLoading] = useState(true);
  const [logError, setLogError] = useState<string | null>(null);

  // Event types for dropdown
  const [eventTypes, setEventTypes] = useState<EventTypeCount[]>([]);

  // Expanded row
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);

  // Diagnostic copy state
  const [copyingId, setCopyingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // ── Data fetching ───────────────────────────────────────────────────────

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const queryStr = hasDateProps
        ? `start_date=${format(startDate!, 'yyyy-MM-dd')}&end_date=${format(endDate!, 'yyyy-MM-dd')}`
        : `days=${days}`;
      const data = await apiFetch<StatusSummary>(
        `/api/admin/llm-health/status-summary?${queryStr}`
      );
      setSummary(data);
    } catch (err: any) {
      setSummaryError(err.message || 'Failed to load status summary');
    } finally {
      setSummaryLoading(false);
    }
  }, [hasDateProps, startDate, endDate, days]);

  const loadErrorLog = useCallback(async () => {
    setLogLoading(true);
    setLogError(null);
    try {
      const params = new URLSearchParams({
        days: String(logDays),
        limit: String(logLimit),
        offset: String(logOffset),
      });
      if (eventTypeFilter) params.set('event_type', eventTypeFilter);
      if (callNameFilter) params.set('call_name', callNameFilter);
      if (modelFilter) params.set('model', modelFilter);

      const data = await apiFetch<ErrorLogResponse>(
        `/api/admin/llm-health/error-log?${params.toString()}`
      );
      setErrorLog(data);
    } catch (err: any) {
      setLogError(err.message || 'Failed to load error log');
    } finally {
      setLogLoading(false);
    }
  }, [logDays, logLimit, logOffset, eventTypeFilter, callNameFilter, modelFilter]);

  const loadEventTypes = useCallback(async () => {
    try {
      const data = await apiFetch<{ event_types: EventTypeCount[] }>(
        `/api/admin/llm-health/event-types?days=${logDays}`
      );
      setEventTypes(data.event_types || []);
    } catch {
      // Non-critical, silently fail
    }
  }, [logDays]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadErrorLog(); }, [loadErrorLog]);
  useEffect(() => { loadEventTypes(); }, [loadEventTypes]);

  // Reset offset when filters change
  useEffect(() => { setLogOffset(0); }, [eventTypeFilter, callNameFilter, modelFilter, logDays]);

  // ── Diagnostic copy handler ─────────────────────────────────────────────

  const copyDiagnostic = async (eventId: string) => {
    setCopyingId(eventId);
    try {
      const data = await apiFetch<any>(
        `/api/admin/llm-health/error-log/${eventId}/diagnostic`
      );
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopiedId(eventId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err: any) {
      console.error('Failed to copy diagnostic:', err);
      alert(`Failed to fetch diagnostic: ${err.message}`);
    } finally {
      setCopyingId(null);
    }
  };

  // ── Chart data ──────────────────────────────────────────────────────────

  const chartData = summary?.by_model.map((m) => ({
    model: m.model.length > 18 ? m.model.slice(0, 16) + '..' : m.model,
    Successful: m.successful_calls,
    Unsuccessful: m.unsuccessful_calls,
  })) || [];

  // ── Pill button helper ────────────────────────────────────────────────

  const Pill: React.FC<{
    label: string;
    active: boolean;
    onClick: () => void;
  }> = ({ label, active, onClick }) => (
    <button
      onClick={onClick}
      style={{
        padding: '6px 16px',
        borderRadius: 20,
        border: active ? `1px solid ${COLORS.accent}` : `1px solid ${COLORS.border}`,
        background: active ? COLORS.accent : 'transparent',
        color: active ? '#fff' : COLORS.textMuted,
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div style={styles.container}>
      {/* ── STATUS SUMMARY ── */}
      <div style={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={styles.sectionTitle}>Status Summary</h3>
          {!hasDateProps && (
            <div style={styles.pillRow}>
              <Pill label="1D" active={days === 1} onClick={() => setDays(1)} />
              <Pill label="7D" active={days === 7} onClick={() => setDays(7)} />
              <Pill label="30D" active={days === 30} onClick={() => setDays(30)} />
            </div>
          )}
        </div>

        {summaryLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
            Loading status summary...
          </div>
        ) : summaryError ? (
          <div style={{ ...styles.card, borderColor: COLORS.red, color: COLORS.red }}>
            {summaryError}
            <button
              onClick={loadSummary}
              style={{
                marginLeft: 12,
                background: 'none',
                border: 'none',
                color: COLORS.red,
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              Retry
            </button>
          </div>
        ) : summary ? (
          <>
            {/* Summary cards */}
            <div style={styles.summaryGrid}>
              <div style={styles.card}>
                <div style={styles.cardLabel}>Total Calls</div>
                <div style={{ ...styles.cardValue, color: COLORS.blue }}>
                  {fmtNum(summary.totals.successful_calls + summary.totals.unsuccessful_calls)}
                </div>
              </div>
              <div style={styles.card}>
                <div style={styles.cardLabel}>Total Errors</div>
                <div style={{ ...styles.cardValue, color: COLORS.red }}>
                  {fmtNum(summary.totals.unsuccessful_calls)}
                </div>
              </div>
              <div style={styles.card}>
                <div style={styles.cardLabel}>Error Rate</div>
                <div style={{ ...styles.cardValue, color: errorRateColor(summary.totals.error_rate_pct) }}>
                  {fmtPct(summary.totals.error_rate_pct)}
                </div>
              </div>
              <div style={styles.card}>
                <div style={styles.cardLabel}>Total Cost</div>
                <div style={{ ...styles.cardValue, color: COLORS.green }}>
                  {fmt(summary.totals.total_cost)}
                </div>
              </div>
            </div>

            {/* Model table */}
            <div style={{ ...styles.card, padding: 0, overflow: 'hidden', marginBottom: 24 }}>
              <table style={styles.table}>
                <thead>
                  <tr style={{ background: '#0f1729' }}>
                    <th style={styles.th}>Model</th>
                    <th style={styles.th}>Successful</th>
                    <th style={styles.th}>Unsuccessful</th>
                    <th style={styles.th}>Error Rate</th>
                    <th style={styles.th}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.by_model.map((m, i) => (
                    <tr
                      key={m.model}
                      style={{
                        background: i % 2 === 0 ? COLORS.card : '#1e2d4d',
                      }}
                    >
                      <td style={{ ...styles.td, fontWeight: 500 }}>{m.model}</td>
                      <td style={styles.td}>{fmtNum(m.successful_calls)}</td>
                      <td style={styles.td}>{fmtNum(m.unsuccessful_calls)}</td>
                      <td style={styles.td}>
                        <span style={styles.badge(errorRateBg(m.error_rate_pct), errorRateColor(m.error_rate_pct))}>
                          {fmtPct(m.error_rate_pct)}
                        </span>
                      </td>
                      <td style={styles.td}>{fmt(m.total_cost)}</td>
                    </tr>
                  ))}
                  {summary.by_model.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ ...styles.td, textAlign: 'center', color: COLORS.textDim }}>
                        No data for this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Chart */}
            {chartData.length > 0 && (
              <div style={{ ...styles.card, padding: '16px 12px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textMuted, marginBottom: 12 }}>
                  Successful vs Unsuccessful by Model
                </div>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartData} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                    <XAxis dataKey="model" tick={{ fill: COLORS.textMuted, fontSize: 11 }} />
                    <YAxis tick={{ fill: COLORS.textMuted, fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{
                        background: COLORS.card,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: 6,
                        color: COLORS.text,
                        fontSize: 12,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12, color: COLORS.textMuted }} />
                    <Bar dataKey="Successful" stackId="a" fill={CHART_COLORS[0]} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Unsuccessful" stackId="a" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* ── ERROR LOG ── */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Error Log</h3>

        {/* Filters */}
        <div style={styles.filterRow}>
          <select
            style={styles.select}
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value)}
          >
            <option value="">All Event Types</option>
            {eventTypes.map((et) => (
              <option key={et.event_type} value={et.event_type}>
                {et.event_type} ({et.count})
              </option>
            ))}
          </select>

          <input
            style={styles.input}
            type="text"
            placeholder="Search call name..."
            value={callNameFilter}
            onChange={(e) => setCallNameFilter(e.target.value)}
          />

          <input
            style={{ ...styles.input, minWidth: 120 }}
            type="text"
            placeholder="Model..."
            value={modelFilter}
            onChange={(e) => setModelFilter(e.target.value)}
          />

          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
            <Pill label="1D" active={logDays === 1} onClick={() => setLogDays(1)} />
            <Pill label="7D" active={logDays === 7} onClick={() => setLogDays(7)} />
            <Pill label="30D" active={logDays === 30} onClick={() => setLogDays(30)} />
          </div>
        </div>

        {logLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
            Loading error log...
          </div>
        ) : logError ? (
          <div style={{ ...styles.card, borderColor: COLORS.red, color: COLORS.red }}>
            {logError}
            <button
              onClick={loadErrorLog}
              style={{
                marginLeft: 12,
                background: 'none',
                border: 'none',
                color: COLORS.red,
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              Retry
            </button>
          </div>
        ) : errorLog ? (
          <>
            <div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
              <table style={styles.table}>
                <thead>
                  <tr style={{ background: '#0f1729' }}>
                    <th style={styles.th}>Timestamp</th>
                    <th style={styles.th}>Call Name</th>
                    <th style={styles.th}>Model</th>
                    <th style={styles.th}>Event Type</th>
                    <th style={{ ...styles.th, minWidth: 200 }}>Error Message</th>
                    <th style={styles.th}>Severity</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {errorLog.events.map((evt, i) => (
                    <React.Fragment key={evt.event_id}>
                      <tr
                        style={{
                          background: i % 2 === 0 ? COLORS.card : '#1e2d4d',
                          cursor: 'pointer',
                        }}
                        onClick={() =>
                          setExpandedEventId(expandedEventId === evt.event_id ? null : evt.event_id)
                        }
                      >
                        <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 12 }}>
                          {formatTimestamp(evt.timestamp)}
                        </td>
                        <td style={{ ...styles.td, fontWeight: 500 }}>{evt.call_name}</td>
                        <td style={{ ...styles.td, fontSize: 12 }}>{evt.model}</td>
                        <td style={styles.td}>
                          <span
                            style={styles.badge(
                              `${COLORS.purple}22`,
                              COLORS.purple
                            )}
                          >
                            {evt.event_type}
                          </span>
                        </td>
                        <td style={{ ...styles.td, fontSize: 12, color: COLORS.textMuted }}>
                          {truncate(evt.error_message || '-', 80)}
                        </td>
                        <td style={styles.td}>
                          <span
                            style={{
                              color: severityColor(evt.severity),
                              fontWeight: 600,
                              fontSize: 12,
                              textTransform: 'uppercase',
                            }}
                          >
                            {evt.severity || '-'}
                          </span>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyDiagnostic(evt.event_id);
                            }}
                            disabled={copyingId === evt.event_id}
                            style={{
                              padding: '4px 10px',
                              borderRadius: 4,
                              border: `1px solid ${COLORS.border}`,
                              background: copiedId === evt.event_id ? COLORS.greenDim : 'transparent',
                              color: copiedId === evt.event_id ? COLORS.green : COLORS.textMuted,
                              fontSize: 11,
                              cursor: copyingId === evt.event_id ? 'wait' : 'pointer',
                              fontWeight: 500,
                              transition: 'all 0.15s',
                            }}
                          >
                            {copyingId === evt.event_id
                              ? 'Loading...'
                              : copiedId === evt.event_id
                              ? 'Copied!'
                              : 'Copy Diagnostic'}
                          </button>
                        </td>
                      </tr>
                      {expandedEventId === evt.event_id && (
                        <tr>
                          <td colSpan={7} style={{ padding: '0 12px 12px 12px', background: i % 2 === 0 ? COLORS.card : '#1e2d4d' }}>
                            <div style={styles.expandedRow}>
                              <div style={{ marginBottom: 8, color: COLORS.text, fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                                Full Error Message
                              </div>
                              <div style={{ marginBottom: 16 }}>
                                {evt.error_message || 'No error message'}
                              </div>
                              {evt.additional_context && Object.keys(evt.additional_context).length > 0 && (
                                <>
                                  <div style={{ marginBottom: 8, color: COLORS.text, fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                                    Additional Context
                                  </div>
                                  <div>{JSON.stringify(evt.additional_context, null, 2)}</div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {errorLog.events.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ ...styles.td, textAlign: 'center', color: COLORS.textDim, padding: 32 }}>
                        No error events found for this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {errorLog.total > logLimit && (
              <div style={styles.pagination}>
                <span>
                  Showing {logOffset + 1}-{Math.min(logOffset + logLimit, errorLog.total)} of{' '}
                  {fmtNum(errorLog.total)}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    disabled={logOffset === 0}
                    onClick={() => setLogOffset(Math.max(0, logOffset - logLimit))}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 6,
                      border: `1px solid ${COLORS.border}`,
                      background: 'transparent',
                      color: logOffset === 0 ? COLORS.textDim : COLORS.text,
                      fontSize: 13,
                      cursor: logOffset === 0 ? 'default' : 'pointer',
                    }}
                  >
                    Previous
                  </button>
                  <button
                    disabled={logOffset + logLimit >= errorLog.total}
                    onClick={() => setLogOffset(logOffset + logLimit)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 6,
                      border: `1px solid ${COLORS.border}`,
                      background: 'transparent',
                      color: logOffset + logLimit >= errorLog.total ? COLORS.textDim : COLORS.text,
                      fontSize: 13,
                      cursor: logOffset + logLimit >= errorLog.total ? 'default' : 'pointer',
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default LLMHealthMonitor;
